import { useEffect, useState } from 'react'
import { getVodService } from '@/shared/getContainer/getVodService'
import { EpisodeItemInterface, Season } from '@/types/vod/VodItemDetails'
import { IVODServiceToya } from '@/shared/types/services/types/vodService/vodServiceInterfaces'
import { fetchItemsAmount } from '@/shared/constants'
import { useVODStore } from '@/shared/store'

const { getItems } = (await getVodService()) as IVODServiceToya

interface ISeasonAndEpisodes {
    seasonId: string
    episodes: EpisodeItemInterface[]
}

interface UseSeasonsAndEpisodesProps {
    seasons: Season[]
    seasonId: string
    episodeId: string
}

interface SeasonsAndEpisodesHook {
    actualSeason: Season | undefined
    actualEpisodes: ISeasonAndEpisodes | undefined
    handleSelectSeason: (season: Season) => Promise<void>
}

export const useSeasonsAndEpisodesToya = ({
    seasons,
    seasonId,
    episodeId,
}: UseSeasonsAndEpisodesProps): SeasonsAndEpisodesHook => {
    const [actualSeason, setActualSeason] = useState<Season | undefined>()
    const [seasonsAndEpisodesLocal, setSeasonsAndEpisodesLocal] = useState<
        ISeasonAndEpisodes[]
    >([])
    const { setSeasonsAndEpisodes, getSeasonsAndEpisodes } = useVODStore()

    const handleSelectSeason = async (season: Season) => {
        const cachedEpisodes = getSeasonsAndEpisodes(season.id) || []
        const episodesMap = new Map(
            cachedEpisodes.map((episode) => [episode.productId, episode])
        )

        let fetchedAll = cachedEpisodes.length > 0
        let offset = cachedEpisodes.length

        while (!fetchedAll) {
            const episodes = await getItems<EpisodeItemInterface>(
                season.id,
                offset,
                fetchItemsAmount
            )
            episodes.forEach((episode) =>
                episodesMap.set(episode.productId, episode)
            )

            fetchedAll =
                episodes.length < fetchItemsAmount || episodesMap.has(episodeId)
            offset += fetchItemsAmount
        }

        setActualSeason(season)

        const uniqueEpisodes = Array.from(episodesMap.values())

        setSeasonsAndEpisodesLocal((prev) => [
            ...prev.filter((item) => item.seasonId !== season.id),
            {
                seasonId: season.id,
                episodes: uniqueEpisodes,
            },
        ])

        setSeasonsAndEpisodes({
            seasonId: season.id,
            episodes: uniqueEpisodes,
        })
    }

    useEffect(() => {
        const lastWatchedSeason = seasons.find(
            (season) => season.id === seasonId
        )
        handleSelectSeason(lastWatchedSeason || actualSeason || seasons[0])
    }, [seasons, seasonId])

    const actualEpisodes = seasonsAndEpisodesLocal.find(
        (season) => season.seasonId === actualSeason?.id
    )

    return {
        actualSeason,
        handleSelectSeason,
        actualEpisodes,
    }
}
